<template>
  <div class="print-pdf">
    <button @click="handlePrint" :disabled="loading" class="print-button">
      {{ loading ? '正在准备...' : '打印PDF' }}
    </button>
  </div>
</template>

<script>
export default {
  name: 'PrintPDF',
  props: {
    // 订单信息
    orderInfo: {
      type: Object,
      required: true,
      default: () => ({})
    },
    // 按分类分组的物品列表
    itemsByCategory: {
      type: Object,
      required: true,
      default: () => ({})
    },
    // 总金额
    totalAmount: {
      type: Number,
      default: 0
    },
    // 总物品数量
    totalItemsCount: {
      type: Number,
      default: 0
    },
    // 按钮文本
    buttonText: {
      type: String,
      default: '打印PDF'
    },
    // 是否禁用
    disabled: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      loading: false
    };
  },
  methods: {
    // 格式化日期
    formatDate(date) {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    },

    // 处理打印
    handlePrint() {
      if (this.disabled || this.loading) {
        return;
      }

      // 检查是否有可打印的内容
      const hasItems = Object.values(this.itemsByCategory).some(items => items && items.length > 0);
      if (!hasItems) {
        this.$message.warning('没有可打印的内容');
        return;
      }

      this.loading = true;

      try {
        // 获取订单信息
        const customerName = this.orderInfo.customer_name || '-';
        const customerPhone = this.orderInfo.customer_phone || '-';
        const orderNumber = this.orderInfo.order_number || '-';
        const rentalDays = this.orderInfo.rental_days || 0;
        const currentDate = this.formatDate(new Date());

        // 构建表格HTML内容
        let tableRowsHtml = '';
        let serialNumber = 1;

        Object.entries(this.itemsByCategory).forEach(([categoryName, items]) => {
          if (items && items.length > 0) {
            items.forEach(item => {
              const subtotal = (item.price * item.quantity * rentalDays).toFixed(2);
              tableRowsHtml += `
                <tr>
                  <td style="border: 1px solid #000; padding: 8px; text-align: center;">${serialNumber++}</td>
                  <td style="border: 1px solid #000; padding: 8px;">${categoryName}</td>
                  <td style="border: 1px solid #000; padding: 8px;">${item.name}</td>
                  <td style="border: 1px solid #000; padding: 8px;">${item.spec_text || ''}</td>
                  <td style="border: 1px solid #000; padding: 8px; text-align: right;">${item.price}</td>
                  <td style="border: 1px solid #000; padding: 8px; text-align: right;">${item.quantity}</td>
                  <td style="border: 1px solid #000; padding: 8px; text-align: right;">${rentalDays}</td>
                  <td style="border: 1px solid #000; padding: 8px; text-align: right;">${subtotal}</td>
                </tr>
              `;
            });
          }
        });

        // 创建一个完整的打印窗口
        const printWindow = window.open('', '_blank', 'width=800,height=600');

        // 写入完整的HTML内容到新窗口
        printWindow.document.write(`
          <!DOCTYPE html>
          <html>
          <head>
            <meta charset="utf-8">
            <title>设备清单</title>
            <style>
              body {
                font-family: Arial, sans-serif;
                padding: 20px;
                color: #000;
              }
              h1 {
                text-align: center;
                margin-bottom: 30px;
              }
              .info-box {
                border: 1px solid #000;
                padding: 15px;
                margin-bottom: 20px;
              }
              .info-item {
                display: flex;
                justify-content: space-between;
                margin-bottom: 8px;
              }
              .info-label {
                font-weight: bold;
              }
              table {
                width: 100%;
                border-collapse: collapse;
                margin-bottom: 20px;
              }
              th {
                background-color: #f2f2f2;
                border: 1px solid #000;
                padding: 10px;
                text-align: center;
              }
              td {
                border: 1px solid #000;
                padding: 10px;
              }
              .total-section {
                margin-top: 30px;
                display: flex;
                justify-content: flex-end;
              }
              .total-content {
                width: 300px;
                text-align: right;
              }
              .total-item {
                display: flex;
                justify-content: space-between;
                margin-bottom: 10px;
                font-weight: bold;
              }
              .grand-total {
                font-size: 18px;
                color: #e74c3c;
                margin-top: 10px;
                padding-top: 10px;
                border-top: 1px solid #000;
              }
              @media print {
                body {
                  padding: 0;
                }
                h1, table, .info-box, .total-section {
                  page-break-inside: avoid;
                }
              }
            </style>
          </head>
          <body>
            <h1>设备清单</h1>

            <div class="info-box">
              <div class="info-item">
                <span class="info-label">客户姓名:</span>
                <span>${customerName}</span>
              </div>
              <div class="info-item">
                <span class="info-label">联系电话:</span>
                <span>${customerPhone}</span>
              </div>
              <div class="info-item">
                <span class="info-label">订单号:</span>
                <span>${orderNumber}</span>
              </div>
              <div class="info-item">
                <span class="info-label">租赁天数:</span>
                <span>${rentalDays} 天</span>
              </div>
              <div class="info-item">
                <span class="info-label">创建日期:</span>
                <span>${currentDate}</span>
              </div>
            </div>

            <table>
              <thead>
                <tr>
                  <th>序号</th>
                  <th>分类</th>
                  <th>设备名称</th>
                  <th>规格</th>
                  <th>单价(元/天)</th>
                  <th>数量</th>
                  <th>天数</th>
                  <th>小计(元)</th>
                </tr>
              </thead>
              <tbody>
                ${tableRowsHtml}
              </tbody>
            </table>

            <div class="total-section">
              <div class="total-content">
                <div class="total-item">
                  <span>设备总数:</span>
                  <span>${this.totalItemsCount} 件</span>
                </div>
                <div class="total-item">
                  <span>预估天数:</span>
                  <span>${rentalDays} 天</span>
                </div>
                <div class="total-item grand-total">
                  <span>总计金额:</span>
                  <span>¥${this.totalAmount.toFixed(2)}</span>
                </div>
              </div>
            </div>
          </body>
          </html>
        `);

        printWindow.document.close();

        // 等待新窗口加载完成后触发打印
        printWindow.onload = () => {
          printWindow.focus();
          printWindow.print();
          // 打印完成后自动关闭窗口
          printWindow.onafterprint = () => {
            printWindow.close();
            this.loading = false;
          };

          // 如果用户取消打印，也关闭窗口
          setTimeout(() => {
            if (!this.loading) return;
            this.loading = false;
          }, 1000);
        };

        this.$message.success('打印预览已打开');
      } catch (error) {
        console.error('打印失败:', error);
        this.$message.error('打印失败，请重试');
        this.loading = false;
      }
    }
  }
};
</script>

<style scoped>
.print-button {
  padding: 4px 12px;
  background-color: #409eff;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;
}

.print-button:hover:not(:disabled) {
  background-color: #66b1ff;
}

.print-button:disabled {
  background-color: #c0c4cc;
  cursor: not-allowed;
}
</style>



// WEBPACK FOOTER //
// src/components/PrintPDF.vue